import { useSyncExternalStore } from "react";

/** How often an open cabinet asks the server which build it serves. */
export const CHECK_INTERVAL_MS = 60_000;

export const UPDATE_NOTICE_TEXT_KEY = "mainLayout.header.updateNotice";

let found = false;
const listeners = new Set<() => void>();

/**
 * Build id of this page: scripts/build.js puts it into the `build-id` meta of
 * index.html, not into the JS (a changing value there would change the JS
 * files of every build). In `npm start` the placeholder stays as is: no id.
 */
export const getOwnBuildId = (): string | undefined => {
	const content = document
		.querySelector('meta[name="build-id"]')
		?.getAttribute("content");
	if (!content || content.startsWith("%")) return undefined;
	return content;
};

/**
 * `<git sha>-<ms>` (or `<ms>` without git) -> short sha and build time.
 * Null when the time part is not a number.
 */
export const parseBuildId = (
	buildId: string | undefined,
): { sha?: string; time: number } | null => {
	if (!buildId) return null;
	const dash = buildId.lastIndexOf("-");
	const timePart = buildId.slice(dash + 1);
	if (!/^\d+$/.test(timePart)) return null;
	const sha = dash > 0 ? buildId.slice(0, dash).slice(0, 7) : undefined;
	return { sha, time: Number(timePart) };
};

export const isNewBuildFound = () => found;

/** Once found, stays until the page reloads: only F5 brings the new build. */
export const markNewBuild = () => {
	if (found) return;
	found = true;
	listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const useNewBuildFound = () =>
	useSyncExternalStore(subscribe, isNewBuildFound);

/** `build` of version.json, or null when the answer is not usable. */
export const parseServedBuildId = (body: unknown): string | null => {
	if (!body || typeof body !== "object") return null;
	const { build } = body as { build?: unknown };
	return typeof build === "string" && build ? build : null;
};

/**
 * Asks for version.json and marks a new build when its id differs from ours.
 * A failed request is not an error here: the connection indicator shows the
 * network, the next check simply tries again.
 */
export const checkServedBuild = async (ownBuildId: string) => {
	if (found) return;
	try {
		const response = await fetch(
			`${process.env.PUBLIC_URL || ""}/version.json?t=${Date.now()}`,
			{ cache: "no-store" },
		);
		if (!response.ok) return;
		const served = parseServedBuildId(await response.json());
		if (served && served !== ownBuildId) markNewBuild();
	} catch {
		// Offline or not JSON: wait for the next check.
	}
};

/** A lazy chunk (JS or CSS) that failed to load: gone after a deploy. */
export const isChunkLoadError = (error: unknown) =>
	error instanceof Error &&
	(error.name === "ChunkLoadError" ||
		/Loading (CSS )?chunk .+ failed/.test(error.message));

/** Test only: back to «nothing found». */
export const resetNewBuildForTest = () => {
	found = false;
};
